import React from 'react';
import { Typography, type TypographyProps } from '@mui/material';
import { typography } from 'design-system/tokens';

/**
 * Figma Design System 2026 text style names.
 * Maps 1:1 to the styles defined in:
 * @see https://www.figma.com/design/LYeg7o8lYMFZD4oPJnGTwn/Design-System-2026?node-id=7-3
 */
export type TextStyleName =
  | 'display-lg'
  | 'display-md'
  | 'h1'
  | 'h2'
  | 'h3'
  | 'h4'
  | 'h5'
  | 'h6'
  | 'body-md'
  | 'body-md-double'
  | 'body-md-bold'
  | 'body-sm'
  | 'body-sm-double'
  | 'body-sm-bold'
  | 'caption-md'
  | 'caption-md-bold'
  | 'caption-sm'
  | 'caption-sm-bold'
  | 'button-lg'
  | 'button-md'
  | 'button-sm';

const TEXT_STYLE_PRESETS: Record<TextStyleName, object> = {
  'display-lg':      typography.presets.display.lg,
  'display-md':      typography.presets.display.md,
  h1:              typography.presets.heading.h1,
  h2:              typography.presets.heading.h2,
  h3:              typography.presets.heading.h3,
  h4:              typography.presets.heading.h4,
  h5:              typography.presets.heading.h5,
  h6:              typography.presets.heading.h6,
  'body-md':         typography.presets.body.md,
  'body-md-double':  typography.presets.body.mdDouble,
  'body-md-bold':    typography.presets.body.mdBold,
  'body-sm':         typography.presets.body.sm,
  'body-sm-double':  typography.presets.body.smDouble,
  'body-sm-bold':    typography.presets.body.smBold,
  'caption-md':      typography.presets.caption.md,
  'caption-md-bold': typography.presets.caption.mdBold,
  'caption-sm':      typography.presets.caption.sm,
  'caption-sm-bold': typography.presets.caption.smBold,
  'button-lg':       typography.presets.button.lg,
  'button-md':       typography.presets.button.md,
  'button-sm':       typography.presets.button.sm,
};

/**
 * Text Props
 *
 * Simplified wrapper around MUI Typography.
 *
 * Prefer `textStyle` over `variant` for new code — it maps directly to the
 * Figma Design System 2026 style names and applies the correct font size,
 * weight, and line height automatically.
 *
 * Note: `component` and `variant` are kept for backwards compatibility:
 * - `component`: The actual HTML element rendered (semantic HTML for SEO/a11y)
 * - `variant`: The MUI style preset (visual styling) — use when not using textStyle
 *
 * When `textStyle` is provided, `variant` defaults to `'inherit'` so MUI's own
 * variant styles do not conflict with the preset.
 */
export interface TextProps {
  /** Text content */
  children: React.ReactNode;
  /**
   * Figma Design System 2026 text style name.
   * Applies the correct fontSize, fontWeight, and lineHeight from the design system.
   * Caller-provided `sx` overrides still take precedence.
   *
   * @example
   * <Text textStyle="body-md">Paragraph</Text>
   * <Text textStyle="h1" component="h1">Heading</Text>
   * <Text textStyle="caption-sm-bold" sx={{ color: 'text.secondary' }}>Label</Text>
   */
  textStyle?: TextStyleName;
  /**
   * HTML element to render (semantic HTML).
   * Determines the actual DOM element for SEO and accessibility.
   */
  component?: 'h1' | 'h2' | 'h3' | 'h4' | 'h5' | 'h6' | 'p' | 'span' | 'div';
  /**
   * MUI Typography variant (visual styling).
   * Prefer `textStyle` for new code. Use this only when you need an MUI-specific
   * variant that has no Figma equivalent, or for backwards-compatible code.
   */
  variant?: TypographyProps['variant'];
  /** Custom styles — always applied last, overriding textStyle presets */
  sx?: TypographyProps['sx'];
  /** Additional CSS class */
  className?: string;
  /** Schema.org itemProp for SEO structured data */
  itemProp?: string;
  /** Test ID for UI layer components */
  'data-testid'?: string;
}

/**
 * Text - Atomic text component
 *
 * Generic text wrapper around MUI Typography with Figma design system integration.
 * Provides consistent text rendering across the app with Manrope font by default.
 *
 * @example
 * // Preferred: use Figma style name
 * <Text textStyle="body-md">Paragraph</Text>
 * <Text textStyle="h2" component="h2">Section heading</Text>
 * <Text textStyle="caption-sm-bold" sx={{ color: 'text.secondary' }}>Label</Text>
 *
 * // Legacy: MUI variant (still supported)
 * <Text variant="body1" component="p">Paragraph</Text>
 */
export default function Text({
  children,
  textStyle,
  component = 'p',
  variant,
  sx,
  className,
  itemProp,
  'data-testid': testId,
}: TextProps) {
  const preset = textStyle ? TEXT_STYLE_PRESETS[textStyle] : undefined;
  const resolvedVariant = variant ?? (textStyle ? 'inherit' : 'body1');

  return (
    <Typography
      component={component}
      variant={resolvedVariant}
      sx={{
        fontFamily: typography.fontFamily.manrope,
        ...preset,
        ...sx,
      }}
      className={className}
      itemProp={itemProp}
      data-testid={testId}
    >
      {children}
    </Typography>
  );
}
